/**
 * @file NotificationService.js
 * @description displays snackbar messages
 *
 * @copyright veriome labs, llc.  - all rights reserved.
 * This file is subject to the terms and conditions defined in
 * file 'LICENSE.md', which is part of this source code package.
 *
 * https://codesandbox.io/s/github/iamhosseindhv/notistack/tree/master/examples/redux-example?file=/App.js:877-1216
 * https://github.com/iamhosseindhv/notistack/issues/120
 * 
 * @author don michael
 * @since Dec 21, 2021 15:23
 * 
 * 02/16/2026 dmichael. revised to display special messages from 
 * returning long-running services. 
 * 
 */
import { useEffect, } from 'react';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';
import { useSnackbar } from 'notistack';

import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';

import { objectDeepCopy, objectExists, objectHas } from 'src/@common/utilities';
import { dequeueMessage } from 'src/store/actions/global.actions';

let displayed = [];

function NotificationService(props) {
    const {
        children
    } = props;

    const dispatch = useDispatch();
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    // start: redux state --------------------------
    const globalState = useSelector((state) => {
        return state.global;
    });

    const { notifications } = globalState;
    // end: redux state -----------------------------

    const addDisplayed = (addKey) => {
        displayed = [...displayed, addKey];
    };

    const removeDisplayed = (deleteKey) => {
        displayed = displayed.filter((key) => {
            return key !== deleteKey;
        });
    };

    // biome-ignore lint/correctness/useExhaustiveDependencies(addDisplayed): <explanation>
    // biome-ignore lint/correctness/useExhaustiveDependencies(removeDisplayed): <explanation>
    useEffect(() => {

        notifications.forEach(({ key, message, options = {}, params = {}, }) => {

            // do nothing if snackbar is already displayed
            if (displayed.includes(key)) return;

            // can't mutate redux state!
            const displayOptions = objectDeepCopy(options);

            displayOptions.action = key => (

                <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                        justifyContent: "flex-end",
                    }}
                >
                    <IconButton
                        key="close"
                        aria-label="Close"
                        color="inherit"
                        onClick={() => closeSnackbar(key)}
                        size="large"
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            );

            if (objectExists(params)) {

                if (objectHas(params, 'variant')) {

                    switch (params.variant) {

                        case 'jobRequest':
                            enqueueSnackbar(message, {
                                variant: 'jobRequest',
                                persist: false,
                                params,
                            })

                            break;

                        case 'jobComplete':
                            enqueueSnackbar(message, {
                                variant: 'jobComplete',
                                persist: true,
                                params,
                            })

                            break;

                        case 'jobError':
                            enqueueSnackbar(message, {
                                variant: 'jobError',
                                persist: true,
                                params,
                            })

                            break;

                        default:
                            enqueueSnackbar(
                                message, {
                                key,
                                ...displayOptions,
                                onClose: (event, reason, myKey) => {
                                    dispatch(dequeueMessage(myKey));
                                    removeDisplayed(myKey);
                                },
                            });
                            break;
                    }
                }

            } else {
                enqueueSnackbar(
                    message, {
                    key,
                    ...displayOptions,
                    onClose: (event, reason, myKey) => {
                        dispatch(dequeueMessage(myKey));
                        removeDisplayed(myKey);
                    },
                });
            }

            // keep track of displayed snackbars
            addDisplayed(key);
        });

    }, [notifications, closeSnackbar, enqueueSnackbar, dispatch]);

    return <>{children}</>
}

NotificationService.propTypes = {
    children: PropTypes.any,
};

export default NotificationService;